import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  adminService,
  ConfigName,
  CfgUnion,
  ConfigHistoryEntry,
  MessagePromptCfg,
  CampaignMessageTemplateCfg,
  FaAnalysisPromptCfg,
  ProspectResearchPromptCfg,
  ExemplarScoringConfigCfg,
  CoachingSettingsCfg,
} from '@/api/services/admin';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { Tabs, TabList, Tab, TabPanel } from '@/components/ui/Tabs';
import { extractPlaceholders } from './_prompt-utils';

// ── Slot metadata ─────────────────────────────────────────────────────────────

type SlotKind = 'Prompt' | 'Template (no LLM)' | 'config';

const slotMeta: Record<ConfigName, { description: string; kind: SlotKind }> = {
  message_prompt: {
    description:
      'LLM prompts for prospect message generation (email, linkedin-connection, linkedin-inmail). Shares a baseContext; each channel has its own instructions block.',
    kind: 'Prompt',
  },
  campaign_message_template: {
    description:
      'Static 401k campaign messages. No LLM call — literal {prospect_first_name} / {fa_name} substitution.',
    kind: 'Template (no LLM)',
  },
  fa_analysis_prompt: {
    description:
      'LLM prompt for the FA analysis pass (per-archetype LinkedIn profile rewrite).',
    kind: 'Prompt',
  },
  prospect_research_prompt: {
    description:
      'LLM prompt for prospect personality research. Has a system message plus two preambles (with vs. without LinkedIn URL) and the long user prompt body.',
    kind: 'Prompt',
  },
  exemplar_scoring_config: {
    description:
      'Numeric tuning for the exemplar prospect-scoring algorithm: distance/career/role weights (must sum to 1.0), tenure cap, NAICS-similarity decay, max search distance, and the role-keyword → seniority level taxonomy.',
    kind: 'config',
  },
  coaching_settings: {
    description:
      'AskArchie coaching: system prompt, per-archetype + per-experience coaching notes, response-length presets (5/3/1 min), RAG knobs (match threshold, query limit), and model identifiers (Claude, OpenAI embedding, ElevenLabs voice).',
    kind: 'config',
  },
};

// 'config' is the unified kind for non-prompt configs (was previously a
// per-config-type label like "Scoring config"). Prompts and the no-LLM
// Template keep their original badges.
const kindBadgeVariant = (kind: SlotKind): 'neutral' | 'warning' | 'primary' =>
  kind === 'Template (no LLM)' ? 'warning' : kind === 'config' ? 'primary' : 'neutral';

const CONFIG_NAMES: ConfigName[] = [
  'message_prompt',
  'campaign_message_template',
  'fa_analysis_prompt',
  'prospect_research_prompt',
  'exemplar_scoring_config',
  'coaching_settings',
];

// ── Helpers ───────────────────────────────────────────────────────────────────

const clone = <T,>(v: T): T => JSON.parse(JSON.stringify(v)) as T;

const asArray = (v: unknown): string[] =>
  Array.isArray(v) ? (v as string[]) : [];

const setAt = <T,>(arr: T[], i: number, val: T): T[] =>
  arr.map((x, idx) => (idx === i ? val : x));

const textareaClass =
  'w-full px-3 py-2 rounded-sm border border-border bg-bg-soft dark:bg-surface text-text-primary placeholder:text-text-muted text-sm font-mono resize-y transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-1 focus:ring-primary-500';

const fieldLabel = 'block text-sm font-medium text-text-primary mb-1';
const subLabel = 'block text-sm font-medium text-text-secondary mb-1';

// ── Line-array editor ─────────────────────────────────────────────────────────
// A string[] field rendered as one item per line. join('\n')/split('\n') only
// round-trips losslessly when no element itself contains a newline. If any does,
// editing here would silently restructure the array — so we lock the field and
// steer the user to the Raw JSON tab instead of corrupting data.
function LineArrayField({
  value,
  rows,
  onChange,
}: {
  value: string[];
  rows: number;
  onChange: (next: string[]) => void;
}) {
  const arr = asArray(value);
  const hasEmbeddedNewline = arr.some((s) => typeof s === 'string' && s.includes('\n'));

  if (hasEmbeddedNewline) {
    return (
      <div className="space-y-1">
        <textarea className={textareaClass} rows={rows} value={arr.join('\n')} disabled readOnly />
        <p className="text-xs text-error-700 dark:text-error-400">
          One or more items contain line breaks, so this field can't be edited line-by-line
          without restructuring the array. Edit it in the <span className="font-medium">Raw JSON</span> tab.
        </p>
      </div>
    );
  }

  return (
    <textarea
      className={textareaClass}
      rows={rows}
      value={arr.join('\n')}
      onChange={(e) => onChange(e.target.value.split('\n'))}
    />
  );
}

// ── Per-cfg field editors (controlled) ────────────────────────────────────────

function MessagePromptEditor({
  cfg,
  onChange,
}: {
  cfg: MessagePromptCfg;
  onChange: (c: MessagePromptCfg) => void;
}) {
  const instr = cfg.instructions ?? { email: [], 'linkedin-connection': [], 'linkedin-inmail': [] };
  const setInstr = (channel: keyof MessagePromptCfg['instructions'], next: string[]) =>
    onChange({ ...cfg, instructions: { ...instr, [channel]: next } });

  return (
    <div className="space-y-5">
      <div>
        <label className={fieldLabel}>System message</label>
        <textarea
          className={textareaClass}
          rows={3}
          value={cfg.systemMessage ?? ''}
          onChange={(e) => onChange({ ...cfg, systemMessage: e.target.value })}
        />
      </div>
      <div>
        <label className={fieldLabel}>Base context</label>
        <LineArrayField
          value={cfg.baseContext}
          rows={15}
          onChange={(next) => onChange({ ...cfg, baseContext: next })}
        />
      </div>
      <div className="space-y-4">
        <h4 className="text-sm font-semibold text-text-primary">Instructions</h4>
        <div>
          <label className={subLabel}>email</label>
          <LineArrayField value={instr.email} rows={15} onChange={(n) => setInstr('email', n)} />
        </div>
        <div>
          <label className={subLabel}>linkedin-connection</label>
          <LineArrayField
            value={instr['linkedin-connection']}
            rows={15}
            onChange={(n) => setInstr('linkedin-connection', n)}
          />
        </div>
        <div>
          <label className={subLabel}>linkedin-inmail</label>
          <LineArrayField
            value={instr['linkedin-inmail']}
            rows={15}
            onChange={(n) => setInstr('linkedin-inmail', n)}
          />
        </div>
      </div>
    </div>
  );
}

function CampaignTemplateEditor({
  cfg,
  onChange,
}: {
  cfg: CampaignMessageTemplateCfg;
  onChange: (c: CampaignMessageTemplateCfg) => void;
}) {
  return (
    <div className="space-y-6">
      {/* LinkedIn group */}
      <div className="space-y-4">
        <h4 className="text-sm font-semibold text-text-primary">LinkedIn</h4>
        <div>
          <label className={subLabel}>linkedinConn</label>
          <Input
            value={cfg.linkedinConn ?? ''}
            onChange={(e) => onChange({ ...cfg, linkedinConn: e.target.value })}
          />
        </div>
        <div>
          <label className={subLabel}>linkedinInmail</label>
          <LineArrayField
            value={cfg.linkedinInmail}
            rows={12}
            onChange={(next) => onChange({ ...cfg, linkedinInmail: next })}
          />
        </div>
        <div>
          <label className={subLabel}>inmailSubjects</label>
          <div className="space-y-2">
            {asArray(cfg.inmailSubjects).map((subject, i) => (
              <Input
                key={i}
                value={subject}
                onChange={(e) =>
                  onChange({ ...cfg, inmailSubjects: setAt(asArray(cfg.inmailSubjects), i, e.target.value) })
                }
              />
            ))}
          </div>
          <p className="text-xs text-text-muted mt-1">
            Add/remove subject lines via the Raw JSON tab.
          </p>
        </div>
        <div>
          <label className={subLabel}>linkedinFollowup</label>
          <LineArrayField
            value={cfg.linkedinFollowup}
            rows={10}
            onChange={(next) => onChange({ ...cfg, linkedinFollowup: next })}
          />
        </div>
      </div>

      {/* Email group */}
      <div className="space-y-4 pt-4 border-t border-border">
        <h4 className="text-sm font-semibold text-text-primary">Email</h4>
        <div>
          <label className={subLabel}>email</label>
          <LineArrayField
            value={cfg.email}
            rows={12}
            onChange={(next) => onChange({ ...cfg, email: next })}
          />
        </div>
        <div>
          <label className={subLabel}>emailSubjects</label>
          <div className="space-y-2">
            {asArray(cfg.emailSubjects).map((subject, i) => (
              <Input
                key={i}
                value={subject}
                onChange={(e) =>
                  onChange({ ...cfg, emailSubjects: setAt(asArray(cfg.emailSubjects), i, e.target.value) })
                }
              />
            ))}
          </div>
          <p className="text-xs text-text-muted mt-1">
            Add/remove subject lines via the Raw JSON tab.
          </p>
        </div>
      </div>
    </div>
  );
}

function FaAnalysisEditor({
  cfg,
  onChange,
}: {
  cfg: FaAnalysisPromptCfg;
  onChange: (c: FaAnalysisPromptCfg) => void;
}) {
  return (
    <div className="space-y-5">
      <div>
        <label className={fieldLabel}>systemMessage</label>
        <textarea
          className={textareaClass}
          rows={3}
          value={cfg.systemMessage ?? ''}
          onChange={(e) => onChange({ ...cfg, systemMessage: e.target.value })}
        />
      </div>
      <div>
        <label className={fieldLabel}>userPrompt</label>
        <LineArrayField
          value={cfg.userPrompt}
          rows={25}
          onChange={(next) => onChange({ ...cfg, userPrompt: next })}
        />
      </div>
    </div>
  );
}

function ProspectResearchEditor({
  cfg,
  onChange,
}: {
  cfg: ProspectResearchPromptCfg;
  onChange: (c: ProspectResearchPromptCfg) => void;
}) {
  return (
    <div className="space-y-5">
      <div>
        <label className={fieldLabel}>systemMessage</label>
        <textarea
          className={textareaClass}
          rows={3}
          value={cfg.systemMessage ?? ''}
          onChange={(e) => onChange({ ...cfg, systemMessage: e.target.value })}
        />
      </div>
      <div>
        <label className={fieldLabel}>preAmbleWLinkedIn</label>
        <Input
          value={cfg.preAmbleWLinkedIn ?? ''}
          onChange={(e) => onChange({ ...cfg, preAmbleWLinkedIn: e.target.value })}
        />
      </div>
      <div>
        <label className={fieldLabel}>preAmbleNoLinkedIn</label>
        <Input
          value={cfg.preAmbleNoLinkedIn ?? ''}
          onChange={(e) => onChange({ ...cfg, preAmbleNoLinkedIn: e.target.value })}
        />
      </div>
      <div>
        <label className={fieldLabel}>userPrompt</label>
        <LineArrayField
          value={cfg.userPrompt}
          rows={25}
          onChange={(next) => onChange({ ...cfg, userPrompt: next })}
        />
      </div>
    </div>
  );
}

// ── Numeric input with local text buffer ──────────────────────────────────────
// type="number" controlled inputs are notoriously fiddly: keeping `value` as a
// number drops intermediate states like "0." while typing. We hold a local
// string buffer, parse on each change, only push valid numbers to the parent,
// and re-sync when the external value changes (restore / Raw-JSON apply).
function NumberInput({
  value,
  onChange,
  step,
  min,
}: {
  value: number;
  onChange: (n: number) => void;
  step?: number;
  min?: number;
}) {
  const [text, setText] = useState<string>(() =>
    Number.isFinite(value) ? String(value) : '',
  );
  useEffect(() => {
    const parsed = parseFloat(text);
    if (!Number.isFinite(parsed) || parsed !== value) {
      setText(Number.isFinite(value) ? String(value) : '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  return (
    <Input
      type="number"
      step={step}
      min={min}
      value={text}
      onChange={(e) => {
        const v = e.target.value;
        setText(v);
        const n = parseFloat(v);
        if (Number.isFinite(n)) onChange(n);
      }}
    />
  );
}

// ── Exemplar-scoring weights validity ─────────────────────────────────────────
const WEIGHTS_SUM_TOLERANCE = 0.005;

function exemplarWeightsCheck(cfg: ExemplarScoringConfigCfg): { sum: number; ok: boolean } {
  const sum = (cfg.distanceWeight ?? 0) + (cfg.careerWeight ?? 0) + (cfg.roleWeight ?? 0);
  return { sum, ok: Math.abs(sum - 1) <= WEIGHTS_SUM_TOLERANCE };
}

function ExemplarScoringEditor({
  cfg,
  onChange,
}: {
  cfg: ExemplarScoringConfigCfg;
  onChange: (c: ExemplarScoringConfigCfg) => void;
}) {
  const { sum, ok } = exemplarWeightsCheck(cfg);
  const map = cfg.seniorityMap ?? {};
  // Sort seniority entries by level desc, then key, for a stable read-only view.
  const seniorityKeys = Object.keys(map).sort(
    (a, b) => (map[b] ?? 0) - (map[a] ?? 0) || a.localeCompare(b),
  );

  return (
    <div className="space-y-6">
      <div className="space-y-3">
        <h4 className="text-sm font-semibold text-text-primary">Weights (must sum to 1.0)</h4>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className={subLabel}>distanceWeight</label>
            <NumberInput
              value={cfg.distanceWeight ?? 0}
              step={0.01}
              onChange={(n) => onChange({ ...cfg, distanceWeight: n })}
            />
          </div>
          <div>
            <label className={subLabel}>careerWeight</label>
            <NumberInput
              value={cfg.careerWeight ?? 0}
              step={0.01}
              onChange={(n) => onChange({ ...cfg, careerWeight: n })}
            />
          </div>
          <div>
            <label className={subLabel}>roleWeight</label>
            <NumberInput
              value={cfg.roleWeight ?? 0}
              step={0.01}
              onChange={(n) => onChange({ ...cfg, roleWeight: n })}
            />
          </div>
        </div>
        <p
          className={
            ok
              ? 'text-xs text-text-muted'
              : 'text-xs font-medium text-error-700 dark:text-error-400'
          }
        >
          Sum: {sum.toFixed(4)}{' '}
          {ok ? '✓' : `— must equal 1.0 (±${WEIGHTS_SUM_TOLERANCE}) before Save is enabled`}
        </p>
      </div>

      <div className="space-y-3 pt-4 border-t border-border">
        <h4 className="text-sm font-semibold text-text-primary">Tuning</h4>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className={subLabel}>tenureCap</label>
            <NumberInput
              value={cfg.tenureCap ?? 0}
              step={1}
              min={0}
              onChange={(n) => onChange({ ...cfg, tenureCap: n })}
            />
          </div>
          <div>
            <label className={subLabel}>naics_decay</label>
            <NumberInput
              value={cfg.naics_decay ?? 0}
              step={0.01}
              onChange={(n) => onChange({ ...cfg, naics_decay: n })}
            />
          </div>
          <div>
            <label className={subLabel}>maxDistance</label>
            <NumberInput
              value={cfg.maxDistance ?? 0}
              step={1}
              min={0}
              onChange={(n) => onChange({ ...cfg, maxDistance: n })}
            />
          </div>
        </div>
      </div>

      <div className="space-y-2 pt-4 border-t border-border">
        <h4 className="text-sm font-semibold text-text-primary">seniorityMap</h4>
        <p className="text-xs text-text-muted">
          Read-only here. To add, remove, or rename role keywords, edit via the{' '}
          <span className="font-medium">Raw JSON</span> tab.
        </p>
        <div className="rounded-md border border-border overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-surface-elevated">
                <th className="text-left px-3 py-2 text-xs font-medium text-text-muted uppercase tracking-wide">
                  Role keyword
                </th>
                <th className="text-left px-3 py-2 text-xs font-medium text-text-muted uppercase tracking-wide w-24">
                  Level
                </th>
              </tr>
            </thead>
            <tbody>
              {seniorityKeys.length === 0 ? (
                <tr>
                  <td colSpan={2} className="px-3 py-3 text-sm text-text-secondary">
                    (empty — add entries via Raw JSON)
                  </td>
                </tr>
              ) : (
                seniorityKeys.map((k) => (
                  <tr key={k} className="border-b border-border last:border-0">
                    <td className="px-3 py-2 font-mono text-text-primary">{k}</td>
                    <td className="px-3 py-2 text-text-secondary tabular-nums">{map[k]}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ── Coaching settings (AskArchie) ─────────────────────────────────────────────

type CoachingRecordField =
  | 'ARCHETYPE_NOTES'
  | 'EXPERIENCE_NOTES'
  | 'LENGTH_HINTS'
  | 'LENGTH_DIRECTIVES'
  | 'LENGTH_TOKENS';

function CoachingSettingsEditor({
  cfg,
  onChange,
}: {
  cfg: CoachingSettingsCfg;
  onChange: (c: CoachingSettingsCfg) => void;
}) {
  const archetypeNotes = cfg.ARCHETYPE_NOTES ?? {};
  const experienceNotes = cfg.EXPERIENCE_NOTES ?? {};
  const hints = cfg.LENGTH_HINTS ?? {};
  const directives = cfg.LENGTH_DIRECTIVES ?? {};
  const tokens = cfg.LENGTH_TOKENS ?? {};
  const archetypeKeys = Object.keys(archetypeNotes);
  const experienceKeys = Object.keys(experienceNotes);
  // Union of length-preset keys across all three records, ordered longest-first
  // (5/3/1 by default). Tolerates a key existing in only one of the three.
  const lengthKeys = Array.from(
    new Set([
      ...Object.keys(hints),
      ...Object.keys(directives),
      ...Object.keys(tokens),
    ]),
  ).sort((a, b) => Number(b) - Number(a));

  const setRecordEntry = <V,>(field: CoachingRecordField, key: string, value: V) =>
    onChange({
      ...cfg,
      [field]: {
        ...((cfg[field] as Record<string, V> | undefined) ?? {}),
        [key]: value,
      },
    } as CoachingSettingsCfg);

  return (
    <div className="space-y-6">
      {/* System prompt */}
      <div className="space-y-3">
        <h4 className="text-sm font-semibold text-text-primary">System prompt</h4>
        <div>
          <label className={subLabel}>basePrompt</label>
          <LineArrayField
            value={cfg.basePrompt}
            rows={20}
            onChange={(next) => onChange({ ...cfg, basePrompt: next })}
          />
        </div>
      </div>

      {/* Identity & frameworks */}
      <div className="space-y-3 pt-4 border-t border-border">
        <h4 className="text-sm font-semibold text-text-primary">Identity &amp; frameworks</h4>
        <div>
          <label className={subLabel}>framework_names</label>
          <LineArrayField
            value={cfg.framework_names}
            rows={8}
            onChange={(next) => onChange({ ...cfg, framework_names: next })}
          />
        </div>
        <div>
          <label className={subLabel}>persona_adaptation</label>
          <LineArrayField
            value={cfg.persona_adaptation}
            rows={6}
            onChange={(next) => onChange({ ...cfg, persona_adaptation: next })}
          />
        </div>
        <div>
          <label className={subLabel}>personalization</label>
          <LineArrayField
            value={cfg.personalization}
            rows={4}
            onChange={(next) => onChange({ ...cfg, personalization: next })}
          />
        </div>
        <div>
          <label className={subLabel}>RAG_CONTENT_INTRO</label>
          <LineArrayField
            value={cfg.RAG_CONTENT_INTRO}
            rows={5}
            onChange={(next) => onChange({ ...cfg, RAG_CONTENT_INTRO: next })}
          />
        </div>
      </div>

      {/* Archetype notes */}
      <div className="space-y-3 pt-4 border-t border-border">
        <div className="flex items-baseline justify-between gap-3 flex-wrap">
          <h4 className="text-sm font-semibold text-text-primary">
            ARCHETYPE_NOTES{' '}
            <span className="text-xs font-normal text-text-muted">({archetypeKeys.length})</span>
          </h4>
          <span className="text-xs text-text-muted">
            Add/remove/rename archetypes via the Raw JSON tab.
          </span>
        </div>
        {archetypeKeys.length === 0 ? (
          <p className="text-xs text-text-secondary">(empty)</p>
        ) : (
          <div className="space-y-3">
            {archetypeKeys.map((k) => (
              <div key={k}>
                <label className={subLabel}>{k}</label>
                <LineArrayField
                  value={archetypeNotes[k]}
                  rows={8}
                  onChange={(next) => setRecordEntry<string[]>('ARCHETYPE_NOTES', k, next)}
                />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Experience notes */}
      <div className="space-y-3 pt-4 border-t border-border">
        <div className="flex items-baseline justify-between gap-3 flex-wrap">
          <h4 className="text-sm font-semibold text-text-primary">
            EXPERIENCE_NOTES{' '}
            <span className="text-xs font-normal text-text-muted">({experienceKeys.length})</span>
          </h4>
          <span className="text-xs text-text-muted">
            Add/remove/rename experience levels via the Raw JSON tab.
          </span>
        </div>
        {experienceKeys.length === 0 ? (
          <p className="text-xs text-text-secondary">(empty)</p>
        ) : (
          <div className="space-y-3">
            {experienceKeys.map((k) => (
              <div key={k}>
                <label className={subLabel}>{k}</label>
                <LineArrayField
                  value={experienceNotes[k]}
                  rows={8}
                  onChange={(next) => setRecordEntry<string[]>('EXPERIENCE_NOTES', k, next)}
                />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Response length presets */}
      <div className="space-y-3 pt-4 border-t border-border">
        <div className="flex items-baseline justify-between gap-3 flex-wrap">
          <h4 className="text-sm font-semibold text-text-primary">Response length presets</h4>
          <span className="text-xs text-text-muted">
            Add/remove length keys via the Raw JSON tab.
          </span>
        </div>
        {lengthKeys.length === 0 ? (
          <p className="text-xs text-text-secondary">(empty)</p>
        ) : (
          <div className="space-y-4">
            {lengthKeys.map((k) => (
              <div key={k} className="rounded-md border border-border p-3 space-y-3">
                <p className="text-sm font-mono font-semibold text-text-primary">{k} min</p>
                <div>
                  <label className={subLabel}>LENGTH_HINTS[{k}]</label>
                  <Input
                    value={hints[k] ?? ''}
                    onChange={(e) =>
                      setRecordEntry<string>('LENGTH_HINTS', k, e.target.value)
                    }
                  />
                </div>
                <div>
                  <label className={subLabel}>LENGTH_DIRECTIVES[{k}]</label>
                  <textarea
                    className={textareaClass}
                    rows={4}
                    value={directives[k] ?? ''}
                    onChange={(e) =>
                      setRecordEntry<string>('LENGTH_DIRECTIVES', k, e.target.value)
                    }
                  />
                </div>
                <div>
                  <label className={subLabel}>LENGTH_TOKENS[{k}]</label>
                  <NumberInput
                    value={tokens[k] ?? 0}
                    step={1}
                    min={0}
                    onChange={(n) => setRecordEntry<number>('LENGTH_TOKENS', k, n)}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* RAG tuning */}
      <div className="space-y-3 pt-4 border-t border-border">
        <h4 className="text-sm font-semibold text-text-primary">RAG tuning</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className={subLabel}>RAG_MATCH_THRESHOLD</label>
            <NumberInput
              value={cfg.RAG_MATCH_THRESHOLD ?? 0}
              step={0.01}
              onChange={(n) => onChange({ ...cfg, RAG_MATCH_THRESHOLD: n })}
            />
          </div>
          <div>
            <label className={subLabel}>RAG_QUERY_LIMIT</label>
            <NumberInput
              value={cfg.RAG_QUERY_LIMIT ?? 0}
              step={1}
              min={0}
              onChange={(n) => onChange({ ...cfg, RAG_QUERY_LIMIT: n })}
            />
          </div>
        </div>
      </div>

      {/* Model identifiers */}
      <div className="space-y-3 pt-4 border-t border-border">
        <h4 className="text-sm font-semibold text-text-primary">Model identifiers</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className={subLabel}>CLAUDE_MODEL</label>
            <Input
              value={cfg.CLAUDE_MODEL ?? ''}
              onChange={(e) => onChange({ ...cfg, CLAUDE_MODEL: e.target.value })}
            />
          </div>
          <div>
            <label className={subLabel}>OPENAI_EMBEDDING_MODEL</label>
            <Input
              value={cfg.OPENAI_EMBEDDING_MODEL ?? ''}
              onChange={(e) =>
                onChange({ ...cfg, OPENAI_EMBEDDING_MODEL: e.target.value })
              }
            />
          </div>
          <div>
            <label className={subLabel}>ELEVENLABS_MODEL</label>
            <Input
              value={cfg.ELEVENLABS_MODEL ?? ''}
              onChange={(e) => onChange({ ...cfg, ELEVENLABS_MODEL: e.target.value })}
            />
          </div>
          <div>
            <label className={subLabel}>ELEVENLABS_VOICE_ID</label>
            <Input
              value={cfg.ELEVENLABS_VOICE_ID ?? ''}
              onChange={(e) =>
                onChange({ ...cfg, ELEVENLABS_VOICE_ID: e.target.value })
              }
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function CfgFieldRenderer({
  name,
  cfg,
  onChange,
}: {
  name: ConfigName;
  cfg: CfgUnion;
  onChange: (c: CfgUnion) => void;
}) {
  switch (name) {
    case 'message_prompt':
      return <MessagePromptEditor cfg={cfg as MessagePromptCfg} onChange={onChange} />;
    case 'campaign_message_template':
      return (
        <CampaignTemplateEditor cfg={cfg as CampaignMessageTemplateCfg} onChange={onChange} />
      );
    case 'fa_analysis_prompt':
      return <FaAnalysisEditor cfg={cfg as FaAnalysisPromptCfg} onChange={onChange} />;
    case 'prospect_research_prompt':
      return (
        <ProspectResearchEditor cfg={cfg as ProspectResearchPromptCfg} onChange={onChange} />
      );
    case 'exemplar_scoring_config':
      return (
        <ExemplarScoringEditor cfg={cfg as ExemplarScoringConfigCfg} onChange={onChange} />
      );
    case 'coaching_settings':
      return (
        <CoachingSettingsEditor cfg={cfg as CoachingSettingsCfg} onChange={onChange} />
      );
  }
}

// ── Download helper ───────────────────────────────────────────────────────────

function downloadJson(name: ConfigName, cfg: CfgUnion) {
  const blob = new Blob([JSON.stringify(cfg, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${name}.json`;
  a.click();
  URL.revokeObjectURL(url);
}

// ── Placeholder chip (click-to-copy) ─────────────────────────────────────────

function PlaceholderChip({ token }: { token: string }) {
  const [copied, setCopied] = useState(false);
  const full = '{' + token + '}';

  const handleClick = () => {
    navigator.clipboard.writeText(full).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1000);
    });
  };

  return (
    <button
      onClick={handleClick}
      title={`Click to copy ${full}`}
      className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-mono bg-surface border border-border text-text-secondary hover:border-primary-500 hover:text-text-primary transition-colors duration-150 cursor-pointer focus:outline-none"
    >
      {copied ? 'Copied!' : full}
    </button>
  );
}

// ── Raw JSON panel (editable) ─────────────────────────────────────────────────

function RawJsonEditor({
  value,
  onApply,
}: {
  value: CfgUnion;
  onApply: (cfg: CfgUnion) => void;
}) {
  const pretty = useMemo(() => JSON.stringify(value, null, 2), [value]);
  const [text, setText] = useState(pretty);
  const [parseError, setParseError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // Re-sync when the underlying draft changes elsewhere (structured edit, restore, save).
  useEffect(() => {
    setText(pretty);
    setParseError(null);
  }, [pretty]);

  const handleApply = () => {
    try {
      const parsed = JSON.parse(text);
      setParseError(null);
      onApply(parsed as CfgUnion);
    } catch (e) {
      setParseError(e instanceof Error ? e.message : 'Invalid JSON');
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const dirty = text !== pretty;

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <Button variant="secondary" size="sm" onClick={handleCopy}>
          {copied ? 'Copied!' : 'Copy JSON'}
        </Button>
        <Button variant="secondary" size="sm" onClick={handleApply} disabled={!dirty}>
          Apply changes
        </Button>
        {dirty && !parseError && (
          <span className="text-xs text-text-muted">
            Edited — click Apply, then Save to persist.
          </span>
        )}
      </div>
      <textarea
        className={`${textareaClass} min-h-[50vh]`}
        spellCheck={false}
        value={text}
        onChange={(e) => setText(e.target.value)}
      />
      {parseError && (
        <p className="text-sm text-error-700 dark:text-error-400">
          Invalid JSON: {parseError}
        </p>
      )}
    </div>
  );
}

// ── Versions panel (config history) ───────────────────────────────────────────

function formatWhen(entry: ConfigHistoryEntry): string {
  const raw = entry.created;
  if (!raw) return '—';
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? String(raw) : d.toLocaleString();
}

function VersionsPanel({
  entries,
  loading,
  error,
  onRetry,
  onRestore,
}: {
  entries: ConfigHistoryEntry[] | null;
  loading: boolean;
  error: Error | null;
  onRetry: () => void;
  onRestore: (entry: ConfigHistoryEntry) => void;
}) {
  // Backend ordering isn't guaranteed; show newest first (config_id desc, then timestamp desc).
  const sorted = useMemo(() => {
    if (!entries) return entries;
    return [...entries].sort((a, b) => {
      const ai = Number(a.config_id);
      const bi = Number(b.config_id);
      if (Number.isFinite(ai) && Number.isFinite(bi) && ai !== bi) return bi - ai;
      const at = new Date(a.created || 0).getTime() || 0;
      const bt = new Date(b.created || 0).getTime() || 0;
      return bt - at;
    });
  }, [entries]);

  if (loading) return <LoadingSpinner />;

  if (error) {
    return (
      <div className="flex items-center gap-3 p-3 rounded-md border border-error-200 dark:border-error-800/30 bg-error-50 dark:bg-error-900/20">
        <p className="text-sm text-error-700 dark:text-error-400 flex-1">
          Couldn't load version history: {error.message}
        </p>
        <Button variant="secondary" size="sm" onClick={onRetry}>
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-border overflow-x-auto">
      <table className="w-full text-left border-collapse text-sm">
        <thead>
          <tr className="border-b border-border bg-surface-elevated">
            <th className="px-4 py-2.5 text-xs font-medium text-text-muted uppercase tracking-wide">Version</th>
            <th className="px-4 py-2.5 text-xs font-medium text-text-muted uppercase tracking-wide">Status</th>
            <th className="px-4 py-2.5 text-xs font-medium text-text-muted uppercase tracking-wide">Created</th>
            <th className="px-4 py-2.5 text-xs font-medium text-text-muted uppercase tracking-wide">Action</th>
          </tr>
        </thead>
        <tbody>
          {!sorted || sorted.length === 0 ? (
            <tr>
              <td colSpan={4} className="px-4 py-6 text-sm text-text-secondary">
                No version history returned for this config.
              </td>
            </tr>
          ) : (
            sorted.map((entry, i) => (
              <tr key={entry.config_id ?? i} className="border-b border-border last:border-0">
                <td className="px-4 py-3 font-mono text-text-primary whitespace-nowrap">
                  {entry.config_id ?? '—'}
                </td>
                <td className="px-4 py-3">
                  {entry.is_active ? (
                    <Badge variant="success">Active</Badge>
                  ) : (
                    <Badge variant="neutral">Inactive</Badge>
                  )}
                </td>
                <td className="px-4 py-3 text-text-secondary whitespace-nowrap">
                  {formatWhen(entry)}
                </td>
                <td className="px-4 py-3">
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={!entry.cfg || entry.is_active}
                    title={
                      !entry.cfg
                        ? 'This history row did not include the config body.'
                        : entry.is_active
                          ? 'This is the active version.'
                          : 'Load this version into the editor (review, then Save to apply).'
                    }
                    onClick={() => onRestore(entry)}
                  >
                    Restore
                  </Button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function Prompts() {
  const [selected, setSelected] = useState<ConfigName | null>(null);
  const [cfg, setCfg] = useState<CfgUnion | null>(null); // server baseline
  const [draft, setDraft] = useState<CfgUnion | null>(null); // editable copy
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [activeTab, setActiveTab] = useState('editor');

  // Save state
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<Error | null>(null);
  const [savedOk, setSavedOk] = useState(false);
  const [restoredNote, setRestoredNote] = useState<string | null>(null);

  // History state
  const [history, setHistory] = useState<ConfigHistoryEntry[] | null>(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState<Error | null>(null);

  // Bumped per request so a slow fetch from a previous config can't overwrite a newer one.
  const historyReqRef = useRef(0);
  const loadHistory = useCallback((name: ConfigName) => {
    const reqId = ++historyReqRef.current;
    setHistoryLoading(true);
    setHistoryError(null);
    adminService
      .getConfigHistory(name)
      .then((data) => {
        if (historyReqRef.current === reqId) setHistory(Array.isArray(data) ? data : []);
      })
      .catch((e) => {
        if (historyReqRef.current === reqId)
          setHistoryError(e instanceof Error ? e : new Error(String(e)));
      })
      .finally(() => {
        if (historyReqRef.current === reqId) setHistoryLoading(false);
      });
  }, []);

  useEffect(() => {
    if (!selected) {
      setCfg(null);
      setDraft(null);
      setError(null);
      setHistory(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    setCfg(null);
    setDraft(null);
    setSaveError(null);
    setSavedOk(false);
    setRestoredNote(null);
    setActiveTab('editor');
    adminService
      .getConfig(selected)
      .then((data) => {
        if (!cancelled) {
          setCfg(data);
          setDraft(clone(data));
        }
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e : new Error(String(e)));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    loadHistory(selected);
    return () => {
      cancelled = true;
    };
  }, [selected, loadHistory]);

  const dirty = useMemo(
    () => !!draft && !!cfg && JSON.stringify(draft) !== JSON.stringify(cfg),
    [draft, cfg],
  );

  // Per-config-type Save preconditions (independent of dirty/error). Today only
  // exemplar_scoring_config has one: weights must sum to ~1.0.
  const saveBlockedReason = useMemo<string | null>(() => {
    if (selected === 'exemplar_scoring_config' && draft) {
      const { ok, sum } = exemplarWeightsCheck(draft as ExemplarScoringConfigCfg);
      if (!ok) {
        return `distance + career + role weights = ${sum.toFixed(4)}; must equal 1.0 (±${WEIGHTS_SUM_TOLERANCE}) to save.`;
      }
    }
    return null;
  }, [selected, draft]);

  const activeVersion = useMemo(
    () => history?.find((e) => e.is_active)?.config_id,
    [history],
  );

  const handleDraftChange = (next: CfgUnion) => {
    setDraft(next);
    setSavedOk(false);
    setSaveError(null);
    setRestoredNote(null);
  };

  const handleReset = () => {
    if (cfg) setDraft(clone(cfg));
    setSaveError(null);
    setSavedOk(false);
    setRestoredNote(null);
  };

  const handleRestore = (entry: ConfigHistoryEntry) => {
    if (!entry.cfg) return;
    setDraft(clone(entry.cfg));
    setActiveTab('editor');
    setSaveError(null);
    setSavedOk(false);
    setRestoredNote(
      `Loaded version ${entry.config_id ?? '(unknown)'} into the editor. Review and Save to make it active.`,
    );
  };

  const handleSave = async () => {
    if (!selected || !draft) return;
    setSaving(true);
    setSaveError(null);
    setSavedOk(false);
    try {
      const result = await adminService.saveConfig(selected, draft);
      // The backend "won't save if there are errors". If it signals that via a
      // 2xx body (ok:false / errors) rather than an HTTP error, treat it as a
      // failure too — otherwise we'd report a false success on a rejected config.
      const errs = result?.errors;
      const hasErrors =
        result?.ok === false ||
        (Array.isArray(errs)
          ? errs.length > 0
          : !!errs && typeof errs === 'object' && Object.keys(errs).length > 0);
      if (hasErrors) {
        const msg = Array.isArray(errs)
          ? errs.join('\n')
          : errs
            ? JSON.stringify(errs, null, 2)
            : 'The backend rejected the config but did not return error details.';
        setSaveError(new Error(msg));
        return;
      }
      setCfg(clone(draft));
      setSavedOk(true);
      setRestoredNote(null);
      loadHistory(selected);
    } catch (e) {
      setSaveError(e instanceof Error ? e : new Error(String(e)));
    } finally {
      setSaving(false);
    }
  };

  // ── List view ──────────────────────────────────────────────────────────────

  if (selected === null) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-semibold text-text-primary">Prompts</h1>
        <Card>
          <CardHeader>
            <CardTitle>Prompt &amp; template configs</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left px-4 py-2 font-medium text-text-secondary w-56">Slot</th>
                  <th className="text-left px-4 py-2 font-medium text-text-secondary">Description</th>
                  <th className="text-left px-4 py-2 font-medium text-text-secondary w-36">Kind</th>
                  <th className="text-left px-4 py-2 font-medium text-text-secondary w-24">Status</th>
                </tr>
              </thead>
              <tbody>
                {CONFIG_NAMES.map((name) => {
                  const meta = slotMeta[name];
                  return (
                    <tr
                      key={name}
                      onClick={() => setSelected(name)}
                      className="border-b border-border last:border-0 hover:bg-surface cursor-pointer transition-colors duration-150"
                    >
                      <td className="px-4 py-3 font-mono text-text-primary whitespace-nowrap">{name}</td>
                      <td className="px-4 py-3 text-text-secondary">{meta.description}</td>
                      <td className="px-4 py-3">
                        <Badge variant={kindBadgeVariant(meta.kind)}>{meta.kind}</Badge>
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant="success">Active</Badge>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ── Detail view ────────────────────────────────────────────────────────────

  const meta = slotMeta[selected];

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <div className="flex items-center gap-1 text-sm text-text-secondary">
        <button
          onClick={() => setSelected(null)}
          className="text-primary-500 dark:text-primary-400 hover:underline focus:outline-none"
        >
          ← Prompts
        </button>
        <span className="mx-1">›</span>
        <span className="text-text-primary font-mono">{selected}</span>
      </div>

      {/* Header row */}
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-semibold text-text-primary font-mono">{selected}</h1>
            <Badge variant={kindBadgeVariant(meta.kind)}>{meta.kind}</Badge>
            <span
              className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-mono bg-surface border border-border text-text-secondary"
              title="Active config_id, from GET /adm/get_config_history."
            >
              version: {activeVersion ?? '—'}
            </span>
            {dirty && (
              <Badge variant="warning">Unsaved changes</Badge>
            )}
          </div>
          <p className="text-sm text-text-secondary max-w-2xl">{meta.description}</p>
        </div>

        {/* Action buttons — right side */}
        <div className="flex items-center gap-2 flex-shrink-0">
          {/* Preview — endpoint pending — needs POST /adm/render_config/{name} {vars} */}
          <Button
            variant="secondary"
            disabled
            title="Preview endpoint pending — needs POST /adm/render_config/{name} {vars}"
          >
            Preview
          </Button>

          {/* Download */}
          <Button
            variant="secondary"
            disabled={!draft}
            onClick={() => draft && downloadJson(selected, draft)}
          >
            Download JSON
          </Button>

          {/* Discard edits */}
          <Button variant="ghost" disabled={!dirty || saving} onClick={handleReset}>
            Discard
          </Button>

          {/* Save */}
          <Button
            variant="primary"
            loading={saving}
            disabled={!dirty || saving || !!error || saveBlockedReason !== null}
            onClick={handleSave}
            title={saveBlockedReason ?? undefined}
          >
            Save
          </Button>
        </div>
      </div>

      {/* Save feedback */}
      {saveError && (
        <div className="p-3 rounded-md border border-error-200 dark:border-error-800/30 bg-error-50 dark:bg-error-900/20">
          <p className="text-sm font-medium text-error-700 dark:text-error-400 mb-0.5">
            Not saved — backend validation rejected the config.
          </p>
          <p className="text-sm text-error-700 dark:text-error-400 whitespace-pre-wrap">
            {saveError.message}
          </p>
        </div>
      )}
      {savedOk && !dirty && (
        <div className="p-3 rounded-md border border-success-200 dark:border-success-800/30 bg-success-50 dark:bg-success-900/20">
          <p className="text-sm text-success-700 dark:text-success-400">
            Saved. Check the Versions tab for the new active version.
          </p>
        </div>
      )}
      {restoredNote && (
        <div className="p-3 rounded-md border border-border bg-surface">
          <p className="text-sm text-text-secondary">{restoredNote}</p>
        </div>
      )}

      {/* Body */}
      {loading && <LoadingSpinner />}

      {!loading && error && (
        <Card>
          <CardContent className="py-6">
            <p className="text-text-primary font-medium mb-1">
              Backend endpoint not yet deployed — viewing disabled
            </p>
            <p className="text-sm text-text-secondary">{error.message}</p>
          </CardContent>
        </Card>
      )}

      {!loading && !error && draft && (
        <Tabs value={activeTab} onChange={setActiveTab}>
          <TabList>
            <Tab value="editor">Editor</Tab>
            <Tab value="versions">Versions</Tab>
            <Tab value="raw">Raw JSON</Tab>
          </TabList>

          {/* ── Editor panel ── */}
          <TabPanel value="editor">
            <div className="space-y-6">
              {/* Placeholder chip strip — click to copy */}
              {extractPlaceholders(draft).length > 0 && (
                <div>
                  <p className="text-xs font-medium text-text-secondary mb-2">Placeholders in this cfg</p>
                  <div className="flex flex-wrap gap-2">
                    {extractPlaceholders(draft).map((token) => (
                      <PlaceholderChip key={token} token={token} />
                    ))}
                  </div>
                </div>
              )}

              {/* Field renderer */}
              <Card>
                <CardContent className="py-6">
                  <CfgFieldRenderer name={selected} cfg={draft} onChange={handleDraftChange} />
                </CardContent>
              </Card>
            </div>
          </TabPanel>

          {/* ── Versions panel ── */}
          <TabPanel value="versions">
            <VersionsPanel
              entries={history}
              loading={historyLoading}
              error={historyError}
              onRetry={() => selected && loadHistory(selected)}
              onRestore={handleRestore}
            />
          </TabPanel>

          {/* ── Raw JSON panel ── */}
          <TabPanel value="raw">
            <RawJsonEditor value={draft} onApply={handleDraftChange} />
          </TabPanel>
        </Tabs>
      )}
    </div>
  );
}
